import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfWatermark } from './PdfWatermark';

const styles = StyleSheet.create({
  page: { padding: 20, fontFamily: 'Helvetica', fontSize: 9 },
  header: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: '#e00000', paddingBottom: 10, marginBottom: 15 },
  headerInfo: { flexDirection: 'column' },
  headerTitle: { fontSize: 18, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', marginBottom: 4, color: '#111111' },
  headerSubtitle: { fontSize: 10, color: '#444444', marginBottom: 2 },
  headerLogoContainer: { flexDirection: 'row', alignItems: 'center' },
  teamName: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#888888', fontStyle: 'italic', marginRight: 10 },
  logo: { height: 40, width: 120, objectFit: 'contain' },
  
  mainRow: { flexDirection: 'row', justifyContent: 'space-between' },
  sideColumn: { width: '31%', flexDirection: 'column' },
  centerColumn: { width: '34%', flexDirection: 'column', alignItems: 'center' },
  
  box: { borderWidth: 1, borderColor: '#cccccc', borderStyle: 'solid', borderRadius: 4, padding: 6, backgroundColor: '#fafafa', marginBottom: 10 },
  boxCenter: { backgroundColor: 'transparent', padding: 2, marginBottom: 5, alignItems: 'center' },
  
  boxTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#e00000', borderBottomWidth: 1, borderBottomColor: '#eeeeee', paddingBottom: 3, marginBottom: 5, textTransform: 'uppercase' },
  boxTitleCenter: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#e00000', marginBottom: 5, textTransform: 'uppercase', textAlign: 'center' },
  
  dataRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3, width: '100%' },
  dataLabel: { color: '#555555', fontSize: 8 },
  dataVal: { fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#000000' },
  dataValGreen: { fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#008800' },
  
  spacer: { height: 6 },
  
  carImageContainer: { height: 200, justifyContent: 'center', alignItems: 'center', marginVertical: 10 },
  carImage: { height: 180, objectFit: 'contain' },
  
  bottomRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, borderTopWidth: 2, borderTopColor: '#eeeeee', paddingTop: 10 },
  bottomBox: { width: '32%', borderWidth: 1, borderColor: '#cccccc', borderStyle: 'solid', borderRadius: 4, padding: 6, backgroundColor: '#fafafa' },
  notesBox: { width: '32%', borderWidth: 1, borderColor: '#ffcccc', borderStyle: 'solid', borderRadius: 4, padding: 6, backgroundColor: '#fff3f3' },
  notesTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#d00000', marginBottom: 5 },
  notesText: { fontSize: 8, color: '#444444', lineHeight: 1.4 },
  footerText: { fontSize: 7, color: '#888888', textAlign: 'center', marginTop: 15, borderTopWidth: 1, borderTopColor: '#eeeeee', paddingTop: 5 }
});

const PDF_LABELS = {
  it: {
    driver: 'Pilota', date: 'Data', track: 'Pista',
    fl: 'Anteriore Sinistra (FL)', fr: 'Anteriore Destra (FR)',
    rl: 'Posteriore Sinistra (RL)', rr: 'Posteriore Destra (RR)',
    coldPress: 'Press. Freddo', hotPress: 'Press. Caldo',
    tyre: 'Gomma', tyreCode: 'Codice', tyreLife: 'Usura',
    spring: 'Molla (Rate)', rideHeight: 'Altezza (Ride)',
    frontBrakes: 'Freni Ant.', rearWing: 'Ala Post.', rearBrakes: 'Freni Post.',
    pads: 'Pastiglie (A/P)',
    diffPreload: 'Diff Precarico', diffLock: 'Diff Bloccaggio',
    fuel: 'Carburante Iniz.', totalWeight: 'Peso Totale',
    electronics: 'Elettronica & Freni', diff: 'Differenziale & Pesi',
    notes: 'Note & Feedback', noNotes: 'Nessuna nota aggiunta.',
    customFields: 'Campi Personalizzati (Generali / Telaio)',
    fbTitle: 'Driver Feedback', fbDriver: 'Driver:',
    entry: 'Ingresso', mid: 'Centro', exit: 'Uscita', fbNotes: 'Note:',
    footer: 'Generato con Motorsport Suite - Tutti i diritti riservati - www.motorsportsuite.com',
  },
  en: {
    driver: 'Driver', date: 'Date', track: 'Track',
    fl: 'Front Left (FL)', fr: 'Front Right (FR)',
    rl: 'Rear Left (RL)', rr: 'Rear Right (RR)',
    coldPress: 'Cold Press.', hotPress: 'Hot Press.',
    tyre: 'Tyre', tyreCode: 'Code', tyreLife: 'Wear',
    spring: 'Spring (Rate)', rideHeight: 'Height (Ride)',
    frontBrakes: 'Front Brakes', rearWing: 'Rear Wing', rearBrakes: 'Rear Brakes',
    pads: 'Pads (F/R)',
    diffPreload: 'Diff Preload', diffLock: 'Diff Lock',
    fuel: 'Initial Fuel', totalWeight: 'Total Weight',
    electronics: 'Electronics & Brakes', diff: 'Differential & Weights',
    notes: 'Notes & Feedback', noNotes: 'No notes added.',
    customFields: 'Custom Fields (General / Chassis)',
    fbTitle: 'Driver Feedback', fbDriver: 'Driver:',
    entry: 'Entry', mid: 'Mid', exit: 'Exit', fbNotes: 'Notes:',
    footer: 'Generated with Motorsport Suite - All rights reserved - www.motorsportsuite.com',
  },
};

const SetupPdf = ({ setupData = {}, setup = {}, vehicleData = {}, teamData = {}, teamLogoBase64, vehicleImageBase64, templateConfig = {}, t, displayDate, driverFeedbacks, language = 'it', showWatermark = true }) => {
  const L = PDF_LABELS[language] || PDF_LABELS.it;
  const ant = setupData.anteriore || {};
  const post = setupData.posteriore || {};
  const diff = setupData.differenziale || {};
  const elec = setupData.elettronica || {};
  const brakes = setupData.freni || {};
  const weights = setupData.weights || {};
  const aero = setupData.aero || {};
  const strategy = setupData.strategia || {};
  const feed = setupData.feedback || {};
  
  const fl = setupData.gomme_ant_sx || {};
  const fr = setupData.gomme_ant_dx || {};
  const rl = setupData.gomme_post_sx || {};
  const rr = setupData.gomme_post_dx || {};
  
  let defaultCarImage = `${window.location.origin}/GT.png`;
  
  const vehicleString = JSON.stringify(vehicleData || {}).toLowerCase();
  const categoryStr = (vehicleData?.categoria || '').toLowerCase();

  if (categoryStr) {
    if (categoryStr === 'legend') defaultCarImage = `${window.location.origin}/LEGEND.png`;
    else if (categoryStr === 'formula') defaultCarImage = `${window.location.origin}/Formula.png`;
    else if (categoryStr === 'nascar') defaultCarImage = `${window.location.origin}/Nascar.png`;
    else if (categoryStr === 'v8-super') defaultCarImage = `${window.location.origin}/V8-Super.png`;
    else if (categoryStr === 'kart') defaultCarImage = `${window.location.origin}/Kart.png`;
    else defaultCarImage = `${window.location.origin}/GT.png`;
  } else {
    if (vehicleString.includes('legend')) defaultCarImage = `${window.location.origin}/LEGEND.png`;
    else if (vehicleString.includes('formula')) defaultCarImage = `${window.location.origin}/Formula.png`;
    else if (vehicleString.includes('nascar')) defaultCarImage = `${window.location.origin}/Nascar.png`;
    else if (vehicleString.includes('v8') || vehicleString.includes('supercar')) defaultCarImage = `${window.location.origin}/V8-Super.png`;
    else if (vehicleString.includes('kart')) defaultCarImage = `${window.location.origin}/Kart.png`;
  }

  const val = (v, unit = '') => (v !== undefined && v !== null && v !== '') ? `${v} ${unit}`.trim() : '-';

  // Smistamento Custom Fields
  const customFieldsByPos = { FL: [], FR: [], RL: [], RR: [], General: [] };
  
  if (templateConfig && Array.isArray(templateConfig)) {
    templateConfig.forEach(module => {
      const config = module.config || {};
      const customFields = module.customFields || config.customFields || [];
      if (customFields && Array.isArray(customFields)) {
        customFields.forEach(cf => {
          const pos = cf.position || 'General';
          if (customFieldsByPos[pos]) {
            // Cerca il valore in tutti i possibili sotto-oggetti
            const fieldValue = 
              ant[cf.key] || post[cf.key] || 
              ant.ammortizzatori?.[cf.key] || post.ammortizzatori?.[cf.key] ||
              fl[cf.key] || fr[cf.key] || rl[cf.key] || rr[cf.key] ||
              diff[cf.key] || elec[cf.key] || brakes[cf.key] || 
              weights[cf.key] || aero[cf.key] || strategy[cf.key] || 
              feed[cf.key] || setupData[cf.key] || '';
              
            customFieldsByPos[pos].push({ ...cf, value: fieldValue });
          }
        });
      }
    });
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {showWatermark && <PdfWatermark />}

        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>{setup.eventName || 'Event'} - {setup.sessionName || 'Session'}</Text>
            <Text style={styles.headerSubtitle}>{vehicleData?.name || vehicleData?.modello || 'Vehicle'} | {L.driver}: {setup.nomePilota || 'N/A'}</Text>
            <Text style={styles.headerSubtitle}>{L.date}: {displayDate} | {L.track}: {setup.trackName || 'N/A'}</Text>
          </View>
          <View style={styles.headerLogoContainer}>
            {teamLogoBase64 ? (
              <Image src={teamLogoBase64} style={styles.logo} allowDangerousPaths />
            ) : (
              <Text style={styles.teamName}>{teamData?.nomeTeam || 'Team Name'}</Text>
            )}
          </View>
        </View>

        {/* MAIN BODY */}
        <View style={styles.mainRow}>
          
          {/* LEFT COLUMN (FL & RL) */}
          <View style={styles.sideColumn}>
            {/* FRONT LEFT */}
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{L.fl}</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.coldPress}</Text><Text style={styles.dataVal}>{val(fl.pFreddo, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.hotPress}</Text><Text style={styles.dataValGreen}>{val(fl.pCaldoMeasured, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Temp (I/M/E)</Text><Text style={styles.dataVal}>{val(fl.tInt)}° | {val(fl.tMed)}° | {val(fl.tExt)}°</Text></View>
              {(fl.tireBrand || fl.tireDimensions || fl.tireCompound) && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyre}</Text><Text style={styles.dataVal}>{[fl.tireBrand, fl.tireDimensions, fl.tireCompound].filter(Boolean).join(' · ')}</Text></View>
              )}
              {fl.codicePneumatico && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyreCode}</Text><Text style={styles.dataVal}>{fl.codicePneumatico}</Text></View>
              )}
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Camber</Text><Text style={styles.dataVal}>{val(ant.camberSX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Caster</Text><Text style={styles.dataVal}>{val(ant.casterSX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Toe</Text><Text style={styles.dataVal}>{val(ant.convergenzaSX, 'mm')} ({ant.toeInOrOutSX || '-'})</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.spring}</Text><Text style={styles.dataVal}>{val(ant.mollaSX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Rate</Text><Text style={styles.dataVal}>{val(ant.rigidezzaBumpstopSX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Range</Text><Text style={styles.dataVal}>{val(ant.distanzaBumpstopSX, 'mm')}</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bump / F. Bump</Text><Text style={styles.dataVal}>{val(ant.ammortizzatori?.bumpSX)} / {val(ant.ammortizzatori?.fastBumpSX)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Reb. / F. Reb.</Text><Text style={styles.dataVal}>{val(ant.ammortizzatori?.reboundSX)} / {val(ant.ammortizzatori?.fastReboundSX)}</Text></View>
              {customFieldsByPos['FL'].length > 0 && <View style={styles.spacer} />}
              {customFieldsByPos['FL'].map((cf, idx) => (
                <View key={`fl-cf-${idx}`} style={styles.dataRow}><Text style={styles.dataLabel}>{cf.label}</Text><Text style={styles.dataVal}>{val(cf.value)}</Text></View>
              ))}
            </View>

            {/* REAR LEFT */}
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{L.rl}</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.coldPress}</Text><Text style={styles.dataVal}>{val(rl.pFreddo, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.hotPress}</Text><Text style={styles.dataValGreen}>{val(rl.pCaldoMeasured, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Temp (I/M/E)</Text><Text style={styles.dataVal}>{val(rl.tInt)}° | {val(rl.tMed)}° | {val(rl.tExt)}°</Text></View>
              {(rl.tireBrand || rl.tireDimensions || rl.tireCompound) && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyre}</Text><Text style={styles.dataVal}>{[rl.tireBrand, rl.tireDimensions, rl.tireCompound].filter(Boolean).join(' · ')}</Text></View>
              )}
              {rl.codicePneumatico && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyreCode}</Text><Text style={styles.dataVal}>{rl.codicePneumatico}</Text></View>
              )}
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Camber</Text><Text style={styles.dataVal}>{val(post.camberSX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Toe</Text><Text style={styles.dataVal}>{val(post.convergenzaSX, 'mm')} ({post.toeInOrOutSX || '-'})</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.spring}</Text><Text style={styles.dataVal}>{val(post.mollaSX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Rate</Text><Text style={styles.dataVal}>{val(post.rigidezzaBumpstopSX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Range</Text><Text style={styles.dataVal}>{val(post.distanzaBumpstopSX, 'mm')}</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bump / F. Bump</Text><Text style={styles.dataVal}>{val(post.ammortizzatori?.bumpSX)} / {val(post.ammortizzatori?.fastBumpSX)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Reb. / F. Reb.</Text><Text style={styles.dataVal}>{val(post.ammortizzatori?.reboundSX)} / {val(post.ammortizzatori?.fastReboundSX)}</Text></View>
              {customFieldsByPos['RL'].length > 0 && <View style={styles.spacer} />}
              {customFieldsByPos['RL'].map((cf, idx) => (
                <View key={`rl-cf-${idx}`} style={styles.dataRow}><Text style={styles.dataLabel}>{cf.label}</Text><Text style={styles.dataVal}>{val(cf.value)}</Text></View>
              ))}
            </View>
          </View>

          {/* CENTER COLUMN */}
          <View style={styles.centerColumn}>
            <View style={styles.boxCenter}>
              <Text style={styles.boxTitleCenter}>FRONT AERO & CHASSIS</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Splitter</Text><Text style={styles.dataVal}>{val(aero.splitterAnteriore, 'mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>ARB Ant.</Text><Text style={styles.dataVal}>{val(ant.barra)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.rideHeight}</Text><Text style={styles.dataVal}>{val(ant.altezzaTargetSX, 'mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.frontBrakes}</Text><Text style={styles.dataVal}>{val(aero.condottiFrenoAnteriori)}</Text></View>
            </View>

            <View style={styles.carImageContainer}>
              <Image 
                src={vehicleImageBase64 ? vehicleImageBase64 : defaultCarImage} 
                style={styles.carImage} 
                allowDangerousPaths 
              />
            </View>

            <View style={styles.boxCenter}>
              <Text style={styles.boxTitleCenter}>REAR AERO & CHASSIS</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.rearWing}</Text><Text style={styles.dataVal}>{val(aero.alaPosteriore)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>ARB Post.</Text><Text style={styles.dataVal}>{val(post.barra)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.rideHeight}</Text><Text style={styles.dataVal}>{val(post.altezzaTargetSX, 'mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.rearBrakes}</Text><Text style={styles.dataVal}>{val(aero.condottiFrenoPosteriori)}</Text></View>
            </View>
          </View>

          {/* RIGHT COLUMN (FR & RR) */}
          <View style={styles.sideColumn}>
            {/* FRONT RIGHT */}
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{L.fr}</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.coldPress}</Text><Text style={styles.dataVal}>{val(fr.pFreddo, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.hotPress}</Text><Text style={styles.dataValGreen}>{val(fr.pCaldoMeasured, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Temp (I/M/E)</Text><Text style={styles.dataVal}>{val(fr.tInt)}° | {val(fr.tMed)}° | {val(fr.tExt)}°</Text></View>
              {(fr.tireBrand || fr.tireDimensions || fr.tireCompound) && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyre}</Text><Text style={styles.dataVal}>{[fr.tireBrand, fr.tireDimensions, fr.tireCompound].filter(Boolean).join(' · ')}</Text></View>
              )}
              {fr.codicePneumatico && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyreCode}</Text><Text style={styles.dataVal}>{fr.codicePneumatico}</Text></View>
              )}
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Camber</Text><Text style={styles.dataVal}>{val(ant.camberDX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Caster</Text><Text style={styles.dataVal}>{val(ant.casterDX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Toe</Text><Text style={styles.dataVal}>{val(ant.convergenzaDX, 'mm')} ({ant.toeInOrOutDX || '-'})</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.spring}</Text><Text style={styles.dataVal}>{val(ant.mollaDX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Rate</Text><Text style={styles.dataVal}>{val(ant.rigidezzaBumpstopDX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Range</Text><Text style={styles.dataVal}>{val(ant.distanzaBumpstopDX, 'mm')}</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bump / F. Bump</Text><Text style={styles.dataVal}>{val(ant.ammortizzatori?.bumpDX)} / {val(ant.ammortizzatori?.fastBumpDX)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Reb. / F. Reb.</Text><Text style={styles.dataVal}>{val(ant.ammortizzatori?.reboundDX)} / {val(ant.ammortizzatori?.fastReboundDX)}</Text></View>
              {customFieldsByPos['FR'].length > 0 && <View style={styles.spacer} />}
              {customFieldsByPos['FR'].map((cf, idx) => (
                <View key={`fr-cf-${idx}`} style={styles.dataRow}><Text style={styles.dataLabel}>{cf.label}</Text><Text style={styles.dataVal}>{val(cf.value)}</Text></View>
              ))}
            </View>

            {/* REAR RIGHT */}
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{L.rr}</Text>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.coldPress}</Text><Text style={styles.dataVal}>{val(rr.pFreddo, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.hotPress}</Text><Text style={styles.dataValGreen}>{val(rr.pCaldoMeasured, 'bar')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Temp (I/M/E)</Text><Text style={styles.dataVal}>{val(rr.tInt)}° | {val(rr.tMed)}° | {val(rr.tExt)}°</Text></View>
              {(rr.tireBrand || rr.tireDimensions || rr.tireCompound) && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyre}</Text><Text style={styles.dataVal}>{[rr.tireBrand, rr.tireDimensions, rr.tireCompound].filter(Boolean).join(' · ')}</Text></View>
              )}
              {rr.codicePneumatico && (
                <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.tyreCode}</Text><Text style={styles.dataVal}>{rr.codicePneumatico}</Text></View>
              )}
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Camber</Text><Text style={styles.dataVal}>{val(post.camberDX, '°')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Toe</Text><Text style={styles.dataVal}>{val(post.convergenzaDX, 'mm')} ({post.toeInOrOutDX || '-'})</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.spring}</Text><Text style={styles.dataVal}>{val(post.mollaDX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Rate</Text><Text style={styles.dataVal}>{val(post.rigidezzaBumpstopDX, 'N/mm')}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bumpstop Range</Text><Text style={styles.dataVal}>{val(post.distanzaBumpstopDX, 'mm')}</Text></View>
              <View style={styles.spacer} />
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Bump / F. Bump</Text><Text style={styles.dataVal}>{val(post.ammortizzatori?.bumpDX)} / {val(post.ammortizzatori?.fastBumpDX)}</Text></View>
              <View style={styles.dataRow}><Text style={styles.dataLabel}>Reb. / F. Reb.</Text><Text style={styles.dataVal}>{val(post.ammortizzatori?.reboundDX)} / {val(post.ammortizzatori?.fastReboundDX)}</Text></View>
              {customFieldsByPos['RR'].length > 0 && <View style={styles.spacer} />}
              {customFieldsByPos['RR'].map((cf, idx) => (
                <View key={`rr-cf-${idx}`} style={styles.dataRow}><Text style={styles.dataLabel}>{cf.label}</Text><Text style={styles.dataVal}>{val(cf.value)}</Text></View>
              ))}
            </View>
          </View>
        </View>

        {/* BOTTOM ROW */}
        <View style={styles.bottomRow}>
          <View style={styles.bottomBox}>
            <Text style={styles.boxTitle}>{L.electronics}</Text>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>Brake Bias</Text><Text style={styles.dataVal}>{val(brakes.bilanciamentoFreni, '%')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>Brake Power</Text><Text style={styles.dataVal}>{val(brakes.potenzaFreni, '%')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.pads}</Text><Text style={styles.dataVal}>{val(brakes.pastiglieFrenoAnteriori)} / {val(brakes.pastiglieFrenoPosteriori)}</Text></View>
            <View style={styles.spacer} />
            <View style={styles.dataRow}><Text style={styles.dataLabel}>TC1 / TC2</Text><Text style={styles.dataVal}>{val(elec.tc1)} / {val(elec.tc2)}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>ABS / ECU</Text><Text style={styles.dataVal}>{val(elec.abs)} / {val(elec.mappaECU)}</Text></View>
          </View>

          <View style={styles.bottomBox}>
            <Text style={styles.boxTitle}>{L.diff}</Text>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.diffPreload}</Text><Text style={styles.dataVal}>{val(diff.precarico, 'Nm')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.diffLock}</Text><Text style={styles.dataVal}>{val(diff.bloccaggio, '%')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.fuel}</Text><Text style={styles.dataVal}>{val(strategy.carburanteIniziale, 'L')}</Text></View>
            <View style={styles.spacer} />
            <View style={styles.dataRow}><Text style={styles.dataLabel}>{L.totalWeight}</Text><Text style={styles.dataVal}>{val(weights.totalWeight, 'kg')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>Ballast</Text><Text style={styles.dataVal}>{val(weights.ballast, 'kg')}</Text></View>
            <View style={styles.dataRow}><Text style={styles.dataLabel}>Cross Weight</Text><Text style={styles.dataVal}>{val(weights.weightRip, '%')}</Text></View>
          </View>

          <View style={styles.notesBox}>
            <Text style={styles.notesTitle}>{L.notes}</Text>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>Best Lap: {val(feed.bestLap)}</Text>
            <Text style={styles.notesText}>{feed.noteTecnico || L.noNotes}</Text>
          </View>
        </View>

        {/* CUSTOM FIELDS GENERALI */}
        {customFieldsByPos['General'].length > 0 && (
          <View style={{ ...styles.bottomRow, marginTop: 10 }}>
            <View style={{ width: '100%', borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, padding: 6, backgroundColor: '#fafafa' }}>
              <Text style={styles.boxTitle}>{L.customFields}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {customFieldsByPos['General'].map((cf, idx) => (
                  <View key={`gen-cf-${idx}`} style={{ width: '33%', marginBottom: 4 }}>
                    <View style={styles.dataRow}>
                      <Text style={styles.dataLabel}>{cf.label}</Text>
                      <Text style={styles.dataVal}>{val(cf.value)}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* FOOTER */}
        <Text style={styles.footerText}>
          {L.footer}
        </Text>

      </Page>

      {/* DRIVER FEEDBACKS PAGE (if any) */}
      {driverFeedbacks && driverFeedbacks.length > 0 && (
        <Page size="A4" style={styles.page}>
          {showWatermark && <PdfWatermark />}
          <View style={styles.header}>
            <View style={styles.headerInfo}>
              <Text style={styles.headerTitle}>{L.fbTitle} - {setup.sessionName || 'Session'}</Text>
              <Text style={styles.headerSubtitle}>{vehicleData?.name || vehicleData?.modello || 'Vehicle'}</Text>
            </View>
            <View style={styles.headerLogoContainer}>
              {teamLogoBase64 ? (
                <Image src={teamLogoBase64} style={styles.logo} allowDangerousPaths />
              ) : (
                <Text style={styles.teamName}>{teamData?.nomeTeam || 'Team Name'}</Text>
              )}
            </View>
          </View>
          
          {driverFeedbacks.map((fb, idx) => (
            <View key={idx} style={{ marginBottom: 15, padding: 10, borderWidth: 1, borderColor: '#e00000', borderRadius: 4, backgroundColor: '#fafafa' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 1, borderBottomColor: '#eeeeee', paddingBottom: 5, marginBottom: 5 }}>
                 <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 10 }}>{L.fbDriver} {fb.driverName || 'N/A'}</Text>
                 <Text style={{ fontSize: 8, color: '#666' }}>{fb.createdAt ? new Date(fb.createdAt.seconds * 1000).toLocaleString() : ''}</Text>
              </View>
              {fb.corners && fb.corners.map((c, cIdx) => (
                <View key={cIdx} style={{ marginBottom: 6, marginLeft: 10, paddingLeft: 5, borderLeftWidth: 2, borderLeftColor: '#e00000' }}>
                  <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{c.name}</Text>
                  <Text style={{ fontSize: 8, color: '#444' }}>{L.entry}: {c.entry}/10  |  {L.mid}: {c.mid}/10  |  {L.exit}: {c.exit}/10</Text>
                  {c.notes && <Text style={{ fontSize: 8, color: '#222', fontStyle: 'italic', marginTop: 2 }}>{L.fbNotes} "{c.notes}"</Text>}
                </View>
              ))}
            </View>
          ))}
          
          <Text style={styles.footerText}>{L.footer}</Text>
        </Page>
      )}

    </Document>
  );
};

export default SetupPdf;
